import React, { useState, useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Container,
  CssBaseline,
  TextField,
  Button,
  Link,
} from "@material-ui/core";
import { Email as EmailIcon } from "@material-ui/icons";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ColorModeContext from "../../layout/themeContext";
import useSettings from "../../hooks/useSettings";
import { getBackendUrl } from "../../config";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100vw",
    height: "100vh",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    padding: "0",
    boxSizing: "border-box",
    overflow: "auto",
    position: "relative",
    backgroundColor: theme.palette.background ? theme.palette.background.default : (theme.palette.type === "light" ? "#f5f5f5" : "#303030"),
    backgroundImage: "none",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    backgroundPosition: "center center",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },

  "@keyframes slideInUp": {
    from: {
      opacity: 0,
      transform: "translateY(30px)",
    },
    to: {
      opacity: 1,
      transform: "translateY(0)",
    },
  },

  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    padding: "20px",
  },

  paper: {
    backgroundColor: "white",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "40px 50px",
    borderRadius: "20px",
    width: "100%",
    maxWidth: "480px",
    boxSizing: "border-box",
    animation: "$slideInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
    [theme.breakpoints.down("md")]: {
      maxWidth: "90%",
      padding: "35px 40px",
    },
    [theme.breakpoints.down("sm")]: {
      maxWidth: "95%",
      padding: "30px 25px",
    },
  },

  logoImg: {
    maxWidth: "140px",
    maxHeight: "60px",
    height: "auto",
    width: "auto",
    marginBottom: "24px",
    objectFit: "contain",
    filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
    display: "block",
    marginLeft: "auto",
    marginRight: "auto",
    content: "url(" + (theme.mode === "light"
      ? theme.calculatedLogoLight()
      : theme.calculatedLogoDark()) + ")",
  },

  iconContainer: {
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    background: `linear-gradient(135deg, ${theme.palette.primary.main}20 0%, ${theme.palette.primary.main}40 100%)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 20px",
  },

  icon: {
    fontSize: "28px",
    color: theme.palette.primary.main,
  },

  title: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#1a1a1a",
    marginBottom: "6px",
    textAlign: "center",
  },

  subtitle: {
    fontSize: "13px",
    color: "#666",
    marginBottom: "24px",
    textAlign: "center",
    lineHeight: "1.6",
  },

  form: {
    width: "100%",
    marginTop: theme.spacing(1),
  },

  textField: {
    marginBottom: "12px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: "#f8f9fa",
      transition: "all 0.3s ease",
      "&:hover": {
        backgroundColor: "#f0f1f3",
      },
      "&.Mui-focused": {
        backgroundColor: "white",
        boxShadow: `0 0 0 3px ${theme.palette.primary.main}15`,
      },
      "& fieldset": {
        borderColor: "#e0e0e0",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: "2px",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "14px 14px",
      fontSize: "14px",
    },
    "& .MuiInputLabel-root": {
      color: "#666",
      fontWeight: 500,
      fontSize: "14px",
      transform: "translate(14px, 15px) scale(1)",
      "&.MuiInputLabel-shrink": {
        transform: "translate(14px, -6px) scale(0.75)",
      },
      "&.Mui-focused": {
        color: theme.palette.primary.main,
      },
    },
  },

  submit: {
    marginTop: "12px",
    marginBottom: "8px",
    background: theme.palette.primary.main,
    color: "white",
    borderRadius: "10px",
    padding: "12px 0",
    fontSize: "15px",
    fontWeight: 600,
    textTransform: "none",
    boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
    border: "none",
    transition: "all 0.3s ease",
    "&:hover": {
      background: theme.palette.primary.dark,
      transform: "translateY(-2px)",
      boxShadow: `0 6px 16px ${theme.palette.primary.main}50`,
    },
    "&:active": {
      transform: "translateY(0)",
    },
  },

  backButton: {
    width: "100%",
    padding: "12px 0",
    borderRadius: "10px",
    border: `2px solid ${theme.palette.primary.main}`,
    backgroundColor: "transparent",
    color: theme.palette.primary.main,
    fontSize: "15px",
    fontWeight: 600,
    cursor: "pointer",
    textDecoration: "none",
    display: "block",
    textAlign: "center",
    marginTop: "8px",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: `${theme.palette.primary.main}08`,
      borderColor: theme.palette.primary.dark,
      color: theme.palette.primary.dark,
    },
  },

  successMessage: {
    background: "#d4edda",
    border: "1px solid #c3e6cb",
    borderRadius: "10px",
    padding: "14px",
    color: "#155724",
    fontSize: "13px",
    marginBottom: "16px",
    textAlign: "center",
    lineHeight: "1.6",
  },
}));

const ForgotPassword = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { colorMode } = useContext(ColorModeContext);
  const { appLogoFavicon, appName } = colorMode;
  const { getPublicSetting } = useSettings();

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [backgroundLight, setBackgroundLight] = useState("");
  const [backgroundDark, setBackgroundDark] = useState("");

  const getCompanyIdFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const companyId = urlParams.get("companyId");
    return companyId ? parseInt(companyId) : null;
  };

  useEffect(() => {
    const companyId = getCompanyIdFromUrl();

    getPublicSetting("appLogoBackgroundLight", companyId)
      .then((bgLight) => {
        if (bgLight) {
          const backendUrl = getBackendUrl();
          const fullUrl = backendUrl + "/public/" + bgLight;
          setBackgroundLight(fullUrl);
        } else {
          setBackgroundLight("");
        }
      })
      .catch((err) => {
        console.error("Erro ao carregar imagem de fundo clara:", err);
        setBackgroundLight("");
      });

    getPublicSetting("appLogoBackgroundDark", companyId)
      .then((bgDark) => {
        if (bgDark) {
          const backendUrl = getBackendUrl();
          const fullUrl = backendUrl + "/public/" + bgDark;
          setBackgroundDark(fullUrl);
        } else {
          setBackgroundDark("");
        }
      })
      .catch((err) => {
        console.error("Erro ao carregar imagem de fundo escura:", err);
        setBackgroundDark("");
      });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error("Digite seu email");
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setEmailSent(true);
      toast.success("Email de recuperação enviado!");
    } catch (error) {
      toast.error("Erro ao enviar email. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const { mode } = colorMode;
  const hasValidLightBg = backgroundLight && backgroundLight.trim() !== '';
  const hasValidDarkBg = backgroundDark && backgroundDark.trim() !== '';
  
  let finalBackground;
  if (mode === "light") {
    finalBackground = hasValidLightBg ? `url(${backgroundLight})` : (theme.palette.background ? theme.palette.background.default : "#f5f5f5");
  } else {
    finalBackground = hasValidDarkBg ? `url(${backgroundDark})` : (theme.palette.background ? theme.palette.background.default : "#303030");
  }

  return (
    <>
      <Helmet>
        <title>{appName || "Multi100"} - Recuperar Senha</title>
        <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"} />
      </Helmet>

      <div 
        className={classes.root}
        style={{
          backgroundImage: typeof finalBackground === "string" && finalBackground.includes("url(") 
            ? finalBackground 
            : "none"
        }}
      >
        <Container component="main" maxWidth={false} className={classes.container}>
          <CssBaseline />
          <div className={classes.paper}>
            <div>
              <img className={classes.logoImg} alt="logo" />
            </div>

            <div className={classes.iconContainer}>
              <EmailIcon className={classes.icon} />
            </div>

            <h1 className={classes.title}>Recuperar Senha</h1>
            <p className={classes.subtitle}>
              Digite seu email cadastrado e enviaremos instruções para redefinir sua senha
            </p>

            {emailSent && (
              <div className={classes.successMessage}>
                ✓ Email enviado com sucesso!<br />
                Verifique sua caixa de entrada e spam.
              </div>
            )}

            <form className={classes.form} onSubmit={handleSubmit}>
              <TextField
                variant="outlined"
                required
                fullWidth
                id="email"
                label="Email"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={classes.textField}
                disabled={emailSent}
              />

              {!emailSent && (
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                  disabled={loading}
                >
                  {loading ? "Enviando..." : "Enviar Email de Recuperação"}
                </Button>
              )}

              <button
                type="button"
                className={classes.backButton}
                onClick={() => history.push("/login")}
              >
                Voltar para Login
              </button>
            </form>
          </div>
        </Container>
      </div>
    </>
  );
};

export default ForgotPassword;
